import { __assign, __rest } from "tslib";
import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Icon from './Icon';
import MenuHeading from './Menu.Heading';
import MenuPanel from './Menu.Panel';
import SelectMenuFilter from './SelectMenu.Filter';
import SelectMenuItem from './SelectMenu.Item';
import oneOfComponentType from '../propTypes/oneOfComponentType';
import useToggle from '../hooks/useToggle';
import './SelectMenu.scss';
import uniqueId from '../utilities/uniqueId';
var DEFAULT_FILTER_DISPLAY_THRESHOLD = 7;
var SelectMenuInputWidths = {
    SMALL: 'small',
    MEDIUM: 'medium',
    LARGE: 'large',
    FULL: 'full'
};
var SelectSizes = { SMALL: 'small', MEDIUM: 'medium' };
var SelectMenu = function (_a) {
    var _b, _c;
    var children = _a.children, className = _a.className, errorMessage = _a.errorMessage, filterThreshold = _a.filterThreshold, inputWidth = _a.inputWidth, isDisabled = _a.isDisabled, isPinnedBottom = _a.isPinnedBottom, isPinnedRight = _a.isPinnedRight, label = _a.label, menuPanelClassName = _a.menuPanelClassName, onChange = _a.onChange, placeholder = _a.placeholder, shouldShowSearch = _a.shouldShowSearch, size = _a.size, statusIndicator = _a.statusIndicator, value = _a.value, props = __rest(_a, ["children", "className", "errorMessage", "filterThreshold", "inputWidth", "isDisabled", "isPinnedBottom", "isPinnedRight", "label", "menuPanelClassName", "onChange", "placeholder", "shouldShowSearch", "size", "statusIndicator", "value"]);
    var _d = useToggle(false), isMenuOpen = _d[0], openMenu = _d[1], closeMenu = _d[2];
    var _e = useState(''), filterValue = _e[0], setFilterValue = _e[1];
    var _f = useState(null), manuallyFocusedIndex = _f[0], setManuallyFocusedIndex = _f[1];
    var buttonRef = useRef(null);
    var buttonId = uniqueId();
    var unfilteredItems = React.Children.toArray(children);
    var isFilterable = unfilteredItems.length >=
        (filterThreshold !== null && filterThreshold !== void 0 ? filterThreshold : DEFAULT_FILTER_DISPLAY_THRESHOLD);
    var selectedItem = unfilteredItems.find(function (item) { return item.props.value === value; });
    var items = unfilteredItems.filter(function (item) {
        var _a;
        if (!filterValue || item.type !== SelectMenuItem) {
            return true;
        }
        else {
            var childElement = item.props.children;
            var filterTerm = void 0;
            if (typeof childElement === 'string' ||
                typeof childElement === 'number') {
                filterTerm = String(childElement).toLowerCase();
            }
            else {
                // Fall back to data-info attribute if children is not a string or number
                filterTerm = (_a = item.props['data-filter-term']) === null || _a === void 0 ? void 0 : _a.toLowerCase();
            }
            return filterTerm === null || filterTerm === void 0 ? void 0 : filterTerm.includes(filterValue.toLowerCase());
        }
    });
    var focusableItems = items.filter(function (item) { return item.type === SelectMenuItem && !item.props.isDisabled; });
    var isFocusable = function (item) {
        return focusableItems.includes(item);
    };
    // Determining which item to focus is difficult.
    // 1. If the user has manually focussed an item (with keyboard or mouse) after the menu has opened
    //    or after changing the filter value, honor it
    // 2. Otherwise, if a filter value has been applied and the selected item meets the filter criteria,
    //    focus the selected item
    // 3. Otherwise, focus the first focusable item
    // 4. Each time the filter value changes, clear the manual focus so that the most relevant item
    //    becomes focused (#2 or #3)
    var selectedIndex = items.findIndex(function (item) { return item === selectedItem; });
    var derivedFocusedIndex = selectedIndex > -1 ? selectedIndex : items.findIndex(isFocusable);
    var focusedIndex = manuallyFocusedIndex !== null && manuallyFocusedIndex !== void 0 ? manuallyFocusedIndex : derivedFocusedIndex;
    var handleMenuClose = function () {
        closeMenu();
        setFilterValue('');
        setManuallyFocusedIndex(null);
    };
    var handleButtonClick = function () {
        var _a;
        if (isMenuOpen) {
            handleMenuClose();
            (_a = buttonRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
        else {
            openMenu();
        }
    };
    var handleFilterValueChange = function (newValue) {
        setFilterValue(newValue);
        setManuallyFocusedIndex(null);
    };
    var handleItemSelect = function (newValue) {
        var _a;
        if (newValue !== value) {
            onChange(newValue);
        }
        handleMenuClose();
        (_a = buttonRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    };
    var focusNextItem = function (nextIndex) {
        if (nextIndex >= items.length) {
            focusNextItem(0);
        }
        else if (isFocusable(items[nextIndex])) {
            setManuallyFocusedIndex(nextIndex);
        }
        else {
            focusNextItem(nextIndex + 1);
        }
    };
    var focusPreviousItem = function (previousIndex) {
        if (previousIndex < 0) {
            focusPreviousItem(items.length - 1);
        }
        else if (isFocusable(items[previousIndex])) {
            setManuallyFocusedIndex(previousIndex);
        }
        else {
            focusPreviousItem(previousIndex - 1);
        }
    };
    var handleMenuKeyDown = function (event) {
        if (!focusableItems.length) {
            return;
        }
        if (event.key === 'Enter' && focusedIndex >= 0) {
            event.preventDefault();
            handleItemSelect(items[focusedIndex].props.value);
        }
        else if (event.key === 'ArrowDown') {
            event.preventDefault();
            focusNextItem(focusedIndex + 1);
        }
        else if (event.key === 'ArrowUp') {
            event.preventDefault();
            focusPreviousItem(focusedIndex - 1);
        }
    };
    var containerClass = classnames('SelectMenu', className);
    var formElementClass = classnames('SelectMenu__formElement', "SelectMenu__formElement--".concat(inputWidth, "Width"));
    var buttonClass = classnames('SelectMenu__button', "SelectMenu__button--".concat(size), {
        'SelectMenu__button--isInvalid': Boolean(errorMessage)
    });
    var panelClass = classnames('SelectMenu__panel', menuPanelClassName, {
        'SelectMenu__panel--isPinnedRight': isPinnedRight,
        'SelectMenu__panel--isPinnedBottom': isPinnedBottom
    });
    var buttonValueClass = classnames('SelectMenu__buttonValue', {
        'SelectMenu__buttonValue--isPlaceholder': !(selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.props.children.length) && placeholder
    });
    return (React.createElement("div", __assign({}, props, { className: containerClass }),
        label && (React.createElement("label", { className: 'SelectMenu__label', htmlFor: (_b = buttonRef.current) === null || _b === void 0 ? void 0 : _b.id }, label)),
        React.createElement("div", { className: formElementClass },
            React.createElement("button", { "aria-expanded": isMenuOpen, className: buttonClass, "data-testid": 'select menu button', disabled: isDisabled, id: buttonId, onClick: handleButtonClick, ref: buttonRef, type: 'button' },
                React.createElement("span", { className: buttonValueClass }, (_c = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.props.children) !== null && _c !== void 0 ? _c : placeholder),
                React.createElement("div", { className: 'u-flex' },
                    statusIndicator || null,
                    React.createElement(Icon, { "aria-hidden": true, className: 'SelectMenu__buttonIcon', "data-testid": 'select menu button icon', type: 'dropdown' }))),
            isMenuOpen && (React.createElement(MenuPanel, { className: panelClass, "data-testid": 'select menu', onClose: handleMenuClose, onKeyDown: handleMenuKeyDown, 
                // Only one element can have focus at a time. When the filter input
                // does not render, the menu div needs focus so that keyboard events
                // can be bound to it. When the filter input does render, the input needs
                // focus so the user can type in it.
                shouldAutoFocus: !isFilterable },
                isFilterable && shouldShowSearch && (React.createElement(SelectMenuFilter, { onChange: handleFilterValueChange, value: filterValue })),
                items.map(function (item, index) {
                    return isFocusable(item)
                        ? React.cloneElement(item, __assign(__assign({}, item.props), { _isFocused: index === focusedIndex, 
                            // inject props that are managed via the `<SelectMenu>` parent
                            _isSelected: index === selectedIndex, _onClick: handleItemSelect, _onMouseEnter: function () { return setManuallyFocusedIndex(index); } }))
                        : item;
                })))),
        errorMessage && (React.createElement("div", { className: 'SelectMenu__errorMessage', role: 'alert' }, errorMessage))));
};
SelectMenu.Item = SelectMenuItem;
SelectMenu.Heading = MenuHeading;
SelectMenu.defaultProps = {
    filterThreshold: DEFAULT_FILTER_DISPLAY_THRESHOLD,
    inputWidth: SelectMenuInputWidths.MEDIUM,
    isDisabled: false,
    isPinnedBottom: false,
    isPinnedRight: false,
    placeholder: 'Select an option',
    shouldShowSearch: true,
    size: SelectSizes.MEDIUM
};
SelectMenu.propTypes = {
    children: oneOfComponentType([MenuHeading, SelectMenuItem]),
    className: PropTypes.string,
    errorMessage: PropTypes.node,
    filterThreshold: PropTypes.number,
    inputWidth: PropTypes.oneOf(Object.values(SelectMenuInputWidths)),
    isDisabled: PropTypes.bool,
    isPinnedBottom: PropTypes.bool,
    isPinnedRight: PropTypes.bool,
    label: PropTypes.node,
    menuPanelClassName: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
    shouldShowSearch: PropTypes.bool,
    size: PropTypes.oneOf(Object.values(SelectSizes)),
    value: PropTypes.oneOfType([
        PropTypes.bool,
        PropTypes.number,
        PropTypes.string,
    ])
};
export default SelectMenu;
//# sourceMappingURL=SelectMenu.js.map